import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import posthog from "posthog-js";
import {
  Button,
  Modal,
  Form,
  Input,
  Icon,
  Confirm,
  Message,
  Label,
  Popup,
} from "semantic-ui-react";
import moment from "moment";
import DataTable from "../../../ui/data-table/DataTable";
import { GridPager } from "../../../pagination/GridPager";
import RowsPerPageSelect from "../../../ui/rows-per-page-select/RowsPerPageSelect";
import CommonPageHeader from "../../../ui/page-header/CommonPageHeader";
import SearchInput from "../../../ui/search-input/SearchInput";
import SelectFilter from "../../../ui/select-filter/SelectFilter";
import ExcelExportButton from "../../../ui/excel-export-button/ExcelExportButton";
import "rc-pagination/assets/index.css";

import * as departmentActions from "../../../../domain/department/departmentActions";
import { Common } from "../../../../../utils";
import { useSubscriptionStatus } from "../../../../../hooks/useSubscriptionStatus";
import SubscriptionRestriction from "../../../subscriptionRestriction/SubscriptionRestriction";
import "./department.css";
import "../../../../domain/documents/Documents.css";

const NAME_MAX_LENGTH = 250;

const Department = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const departmentReducer = useSelector((state) => state.departmentReducer);
  const { isSuspended } = useSubscriptionStatus();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingDepartment, setDeletingDepartment] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [exportLoading, setExportLoading] = useState(false);

  const { departmentRecord, isLoading, Error, message } = departmentReducer;
  const totalDepartments = Array.isArray(departmentRecord) ? departmentRecord.length : 0;
  const filteredDepartments = useMemo(() => {
    const list = Array.isArray(departmentRecord) ? departmentRecord : [];
    const q = searchInput.trim().toLowerCase();
    return list.filter((department) => {
      const matchesSearch =
        q.length === 0 ||
        String(department.name || "").toLowerCase().includes(q) ||
        String(department.notes || "").toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" || (statusFilter === "active" ? department.isActive : !department.isActive);
      return matchesSearch && matchesStatus;
    });
  }, [departmentRecord, searchInput, statusFilter]);
  const totalFiltered = filteredDepartments.length;
  const startRow = totalFiltered === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalFiltered);
  const paginationSummary =
    totalFiltered <= 0
      ? "No results"
      : totalFiltered === 1
        ? "Showing 1 of 1"
        : startRow === endRow
          ? `Showing ${startRow} of ${totalFiltered}`
          : `Showing ${startRow}-${endRow} of ${totalFiltered}`;

  const sortedDepartments = useMemo(() => {
    const list = [...filteredDepartments];
    const direction = sortDir === "asc" ? 1 : -1;

    const valueForSort = (department) => {
      switch (sortKey) {
        case "name":
          return String(department.name || "").toLowerCase();
        case "description":
          return String(department.notes || "").toLowerCase();
        case "createdDate": {
          const ts = department.createdDate ? new Date(department.createdDate).getTime() : 0;
          return Number.isNaN(ts) ? 0 : ts;
        }
        case "status":
          return department.isActive ? 1 : 0;
        case "userCount":
          return Number(department.userCount) || 0;
        default:
          return String(department[sortKey] || "").toLowerCase();
      }
    };

    list.sort((a, b) => {
      const va = valueForSort(a);
      const vb = valueForSort(b);
      if (va < vb) return -1 * direction;
      if (va > vb) return 1 * direction;
      return 0;
    });

    return list;
  }, [filteredDepartments, sortKey, sortDir]);

  const pagedDepartments = useMemo(() => {
    const list = sortedDepartments;
    const start = (currentPage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [sortedDepartments, currentPage, pageSize]);

  // Redirect to billing if subscription is suspended
  useEffect(() => {
    if (isSuspended) {
      history.replace("/settings/company?tab=billing");
    }
  }, [isSuspended, history]);

  useEffect(() => {
    if (!isSuspended) {
      loadDepartments();
    }
  }, []);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalFiltered / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [totalFiltered, pageSize, currentPage]);

  // Show restriction message if subscription is suspended
  if (isSuspended) {
    return (
      <div style={{ padding: "20px" }}>
        <SubscriptionRestriction actionKey="app.subscriptionRestriction.actionAccessSettings" />
      </div>
    );
  }

  const loadDepartments = () => {
    const options = { start: 0, page: 1, limit: 100 };
    dispatch(departmentActions.DepartmentListing(options, 1));
  };

  const handleInputChange = (e, { name, value }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Real-time validation for notes field
    if (name === "notes" && value) {
      if (!/^[a-zA-Z0-9\s.,!?-]*$/.test(value)) {
        setFormErrors((prev) => ({
          ...prev,
          notes:
            t("app.departments.notesValidation"),
        }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t("app.departments.nameRequired");
    } else if (formData.name.length > NAME_MAX_LENGTH) {
      errors.name = t("app.common.nameMaxChars", { max: NAME_MAX_LENGTH });
    }

    // Check for special characters in notes (only allow letters, numbers, spaces, and basic punctuation)
    if (formData.notes && !/^[a-zA-Z0-9\s.,!?-]*$/.test(formData.notes)) {
      errors.notes =
        t("app.departments.notesValidation");
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      let response;
      if (editingDepartment) {
        response = await dispatch(
          departmentActions.update({
            ...formData,
            id: editingDepartment.id,
          })
        );

        // Success check for update - API returns { success, message, data }
        if (response?.success) {
          Common.showSuccessMessage(
            response?.message || t("app.departments.successUpdated")
          );
          handleCloseModal();
        } else {
          Common.showErrorMessage(response?.message || "Update failed");
        }
      } else {
        response = await dispatch(departmentActions.saveDepartment(formData));

        // Success check for create - API returns { success, message, data }
        if (response?.success) {
          Common.showSuccessMessage(
            response?.message || t("app.departments.successCreated")
          );
          handleCloseModal();
        } else {
          Common.showErrorMessage(response?.message || "Create failed");
        }
      }
    } catch (error) {
      Common.showErrorMessage(t("app.departments.errorGeneric"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      notes: department.notes,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (department) => {
    try {
      await dispatch(departmentActions.Delete(department));
      Common.showSuccessMessage(t("app.departments.successDeactivated"));
      // No need to call loadDepartments() - state is updated automatically via reducer
    } catch (error) {
      Common.showErrorMessage(t("app.departments.errorDeactivate"));
    }
  };

  const handleReactivate = async (department) => {
    try {
      await dispatch(
        departmentActions.update({
          ...department,
          isActive: true,
        })
      );
      Common.showSuccessMessage(t("app.departments.successReactivated"));
      // No need to call loadDepartments() - state is updated automatically via reducer
    } catch (error) {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("departmentReactivate", {
          type: "exception",
          success: false,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
          status: error?.response?.status,
        });
      }
      Common.showErrorMessage(t("app.departments.errorReactivate"));
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDepartment(null);
    setFormData({ name: "", notes: "" });
    setFormErrors({});
  };

  const openCreateModal = () => {
    setEditingDepartment(null);
    setFormData({ name: "", notes: "" });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleSortColumn = (key) => {
    if (key === "actions") return;
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const handleExportDepartments = () => {
    try {
      setExportLoading(true);
      const rows = [
        ["Name", "Description", "Created Date", "Status", "Associated Employees"],
        ...sortedDepartments.map((d) => [
          d.name || "",
          d.notes || "",
          d.createdDate ? new Date(d.createdDate).toLocaleDateString() : "N/A",
          d.isActive ? "Active" : "Inactive",
          String(d.userCount != null ? Number(d.userCount) : 0),
        ]),
      ];
      const csv = rows
        .map((row) =>
          row
            .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "departments.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  };

  const tableColumns = [
    {
      key: "name",
      header: t("app.departments.departmentName"),
      render: (department) => department.name,
    },
    {
      key: "description",
      header: t("app.departments.descriptionCol"),
      render: (department) => (
        <div className="department-description">
          {department.notes || t("app.departments.noDescription")}
        </div>
      ),
    },
    {
      key: "createdDate",
      header: t("app.departments.createdDate"),
      render: (department) => (
        <div className="department-date">
          {department.createdDate
            ? new Date(department.createdDate).toLocaleDateString()
            : t("app.departments.na")}
        </div>
      ),
    },
    {
      key: "status",
      header: t("app.departments.status"),
      render: (department) => (
        <Label color={department.isActive ? "green" : "red"} size="tiny">
          {department.isActive ? t("app.users.active") : t("app.departments.inactive")}
        </Label>
      ),
    },
    {
      key: "userCount",
      header: t("app.departments.associatedEmployees"),
      render: (department) => (
        <div className="department-user-count">
          {department.userCount != null && department.userCount !== undefined
            ? Number(department.userCount)
            : 0}
        </div>
      ),
    },
    {
      key: "actions",
      header: t("app.common.actions"),
      render: (department) => (
        <div className="department-actions">
          <Popup
            content={t("app.departments.editDepartment")}
            trigger={
              <button
                type="button"
                className="documents-grid-action-btn documents-grid-action-btn--edit"
                onClick={() => handleEdit(department)}
              >
                Edit
              </button>
            }
          />
          {department.isActive ? (
            <Popup
              content={t("app.departments.deactivateDepartment")}
              trigger={
                <button
                  type="button"
                  className="documents-grid-action-btn documents-grid-action-btn--delete"
                  onClick={() => {
                    setDeletingDepartment(department);
                    setIsDeleteModalOpen(true);
                  }}
                >
                  Disable
                </button>
              }
            />
          ) : (
            <Popup
              content={t("app.departments.reactivateDepartment")}
              trigger={
                <button
                  type="button"
                  className="documents-grid-action-btn documents-grid-action-btn--view"
                  onClick={() => handleReactivate(department)}
                >
                  Enable
                </button>
              }
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="documents-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        barClassName="documents-header-bar"
        contentClassName="documents-header-content"
        leftClassName="documents-header-left"
        iconWrapperClassName="documents-header-icon-wrapper"
        textClassName="documents-header-text"
        titleClassName="documents-page-title"
        descriptionClassName="documents-page-description"
        rightClassName="documents-header-right"
        icon={<Icon name="building" className="m-0" />}
        title={t("app.departments.title")}
        description={t("app.departments.description")}
        rightContent={
          <>
            <div className="common-page-header-stat-card" title="Total departments">
              <span className="common-page-header-stat-label">Total departments</span>
              <span className="common-page-header-stat-value">{totalDepartments}</span>
            </div>
            <Button
              primary
              onClick={openCreateModal}
              className="create-department-btn"
            >
              <Icon name="plus" />
              {t("app.departments.addDepartment")}
            </Button>
          </>
        }
      />

      {/* Error Message */}
      {Error && (
        <Message negative>
          <Message.Header>{t("app.departments.errorHeader")}</Message.Header>
          <p>{message}</p>
        </Message>
      )}

      {/* Department List */}

      <div className="documents-list-content">
        <div className="department-list-description">
          {isLoading ? (
            <div className="loading-container">
              <Icon name="spinner" loading size="large" />
              <p>{t("app.departments.loadingDepartments")}</p>
            </div>
          ) : totalDepartments > 0 ? (
            <div className="table-container">
              <div className="documents-filter-bar department-grid-toolbar">
                <div className="documents-filter-bar-left">
                  <SearchInput
                    value={searchInput}
                    onChange={(next) => {
                      setSearchInput(next);
                      setCurrentPage(1);
                    }}
                    onClear={() => {
                      setSearchInput("");
                      setCurrentPage(1);
                    }}
                    placeholder="Search department..."
                    ariaLabel="Search departments"
                    wrapperClassName="documents-filter-search-wrap"
                    inputClassName="documents-input documents-filter-search-input"
                    iconClassName="bi bi-search documents-filter-search-icon"
                    clearButtonClassName="documents-filter-search-clear"
                  />
                  <SelectFilter
                    value={statusFilter}
                    onChange={(next) => {
                      setStatusFilter(next);
                      setCurrentPage(1);
                    }}
                    options={[
                      { value: "all", label: "All statuses" },
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                    ariaLabel="Filter by status"
                    wrapperClassName="documents-filter-office"
                    iconClassName="bi bi-funnel documents-filter-office-icon"
                    selectClassName="documents-input documents-filter-select"
                  />
                </div>
                <div className="documents-filter-bar-right">
                  <ExcelExportButton
                    className="documents-compliance-export-btn"
                    onClick={handleExportDepartments}
                    loading={exportLoading}
                    disabled={exportLoading}
                    title="Export departments to CSV"
                    label="Excel"
                    loadingLabel="Exporting..."
                  />
                  <RowsPerPageSelect
                    value={pageSize}
                    onChange={(next) => {
                      setPageSize(next);
                      setCurrentPage(1);
                    }}
                    options={[10, 20, 50, 100]}
                    label="Rows per page"
                    ariaLabel="Rows per page"
                    wrapperClassName="documents-filter-page-size"
                    labelClassName="documents-filter-page-size-label"
                    selectClassName="documents-input documents-filter-select documents-filter-page-size-ddl"
                  />
                </div>
              </div>
              <DataTable
                className="department-table"
                columns={tableColumns.map((column) => ({
                  key: column.key,
                  label: column.header,
                  sortable: column.key !== "actions",
                  headerClassName: column.key === "actions" ? "data-table-th-actions department-table-th-actions" : undefined,
                }))}
                rows={pagedDepartments}
                getRowKey={(department) => department.id}
                getRowClassName={(_, index) => (((currentPage - 1) * pageSize + index) % 2 === 0 ? "even-row" : "odd-row")}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSortColumn}
                emptyMessage={t("app.departments.noDepartmentsFound")}
                renderRow={(department) => (
                  <>
                    {tableColumns.map((column) => (
                      <td
                        key={`${department.id}-${column.key}`}
                        className={column.key === "actions" ? "data-table-td-actions department-table-td-actions" : undefined}
                      >
                        {column.render ? column.render(department) : department[column.key]}
                      </td>
                    ))}
                  </>
                )}
              />
              <div className="documents-pagination documents-pagination--grid-pager">
                <span className="documents-pagination-total">
                  {paginationSummary}
                </span>
                <div className="documents-pagination-pager-wrap">
                  <GridPager
                    className="documents-rc-pagination"
                    onChange={(page) => setCurrentPage(page)}
                    current={currentPage}
                    total={totalFiltered}
                    pageSize={pageSize}
                    defaultPageSize={pageSize}
                    showLessItems
                    showTitle={false}
                    showSizeChanger={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <Icon name="building outline" size="huge" color="grey" />
              <h3>{t("app.departments.noDepartmentsFound")}</h3>
              <p>{t("app.departments.emptyStateDescription")}</p>
            </div>
          )}
        </div>
      </div>


      {/* Create/Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        size="small"
        className="department-modal"
        closeOnEscape={true}
        closeOnDimmerClick={false}
        dimmer={{ className: "department-modal-dimmer" }}
        style={{
          height: "fit-content",
          margin: "auto",
          borderRadius: "20px",
        }}
      >
        <Modal.Header className="modal-header-brand modal-header-brand-gradient">
          <Icon name={editingDepartment ? "edit" : "plus"} />
          <span>
            {editingDepartment ? t("app.departments.editDepartmentTitle") : t("app.departments.createNewDepartment")}
          </span>
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label htmlFor="department-name">{t("app.departments.departmentNameRequired")}</label>
              <Input
                id="department-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t("app.departments.enterDepartmentName")}
                error={!!formErrors.name}
                autoFocus={!editingDepartment}
                maxLength={NAME_MAX_LENGTH}
              />
              <div className="field-helper-text" style={{ textAlign: "right", marginTop: "4px" }}>
                <small className="text-muted">
                  {t("app.common.characterCount", {
                    current: formData.name.length,
                    max: NAME_MAX_LENGTH,
                  })}
                </small>
              </div>
              {formErrors.name && (
                <Message negative size="tiny">
                  {formErrors.name}
                </Message>
              )}
            </Form.Field>
            <Form.Field>
              <label htmlFor="department-description">{t("app.departments.descriptionLabel")}</label>
              <Form.TextArea
                id="department-description"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder={t("app.departments.enterDepartmentDescription")}
                rows={3}
                error={!!formErrors.notes}
                maxLength={500}
              />
              <div className="field-helper-text">
                <small>
                  {t("app.departments.notesHelper")}
                </small>
              </div>
              {formErrors.notes && (
                <Message negative size="tiny">
                  {formErrors.notes}
                </Message>
              )}
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCloseModal} basic color="grey">
            {t("app.common.cancel")}
          </Button>
          <Button
            primary
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {editingDepartment ? t("app.departments.update") : t("app.departments.create")}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Confirm
        open={isDeleteModalOpen}
        header={t("app.departments.confirmDeactivate")}
        content={t("app.departments.confirmDeactivateMessage", { name: deletingDepartment?.name || "" })}
        cancelButton={t("app.common.cancel")}
        confirmButton={t("app.departments.confirmDeactivateButton")}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeletingDepartment(null);
        }}
        onConfirm={() => {
          if (deletingDepartment) {
            handleDelete(deletingDepartment);
            setIsDeleteModalOpen(false);
            setDeletingDepartment(null);
          }
        }}
        size="small"
        className="delete-confirm-modal"
        dimmer={{ className: "department-modal-dimmer" }}
      />
    </div>
  );
};

export default Department;
